import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ClipboardList, Upload, Download, RefreshCw, Search, X, Settings2, AlertTriangle,
  ChevronLeft, ChevronRight, Check, Package, TrendingDown, TrendingUp, CheckCircle2,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  opnameModuleInfo, opnameMasterInfo, opnameUploadMaster, opnameRows, opnameListDiff,
  opnameStats, opnameFacets, opnameSaveCount, opnameSettingsGet, opnameSettingsUpdate,
} from '../lib/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const NORMALIZE = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const HEADER_MAP = {
  kode: ['kode', 'kodeitem', 'kodeproduk', 'sku', 'itemcode'],
  nama: ['nama', 'namaitem', 'namaproduk', 'item', 'produk', 'nama_item'],
  jenis: ['jenis', 'kategori', 'category', 'type'],
  rak: ['rak', 'lokasi', 'rack', 'shelf'],
  stok: ['stok', 'stock', 'qty', 'quantity', 'jumlah'],
  satuan: ['satuan', 'unit', 'uom'],
};
const detectColumns = (headerRow) => {
  const map = {};
  headerRow.forEach((h, i) => {
    const norm = NORMALIZE(h);
    for (const key of Object.keys(HEADER_MAP)) {
      if (map[key] === undefined && HEADER_MAP[key].some((v) => v === norm || norm.startsWith(v))) {
        map[key] = i;
      }
    }
  });
  return map;
};

// Cari baris header dalam 15 baris pertama (untuk file dengan title/subtitle di atas).
// Baris header = memiliki minimal kolom 'kode'.
const findHeaderRow = (aoa) => {
  const limit = Math.min(aoa.length, 15);
  for (let i = 0; i < limit; i++) {
    const row = aoa[i] || [];
    const cols = detectColumns(row.map((v) => String(v || '').trim()));
    if (cols.kode !== undefined) return { rowIndex: i, cols };
  }
  return { rowIndex: -1, cols: {} };
};

/* ---------------- Upload Master Dialog ---------------- */
function UploadMasterDialog({ open, onClose, hasExisting, onUploaded }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) { setFile(null); setPreview(null); setConfirmText(''); }
  }, [open]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error('Maks 20 MB'); e.target.value = ''; return; }
    setFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!aoa.length) throw new Error('Sheet kosong');
      const { rowIndex: headerIdx, cols } = findHeaderRow(aoa);
      if (headerIdx < 0 || cols.kode === undefined) {
        toast.error('Kolom "Kode Item" tidak ditemukan di header (dicek 15 baris pertama)');
        setPreview(null); return;
      }
      const header = (aoa[headerIdx] || []).map((h) => String(h || '').trim());
      const detected = Object.keys(cols);
      const ignored = header.filter((_, i) => !Object.values(cols).includes(i)).filter(Boolean);
      const rows = [];
      const seen = new Map();
      let duplicates = 0;
      for (let i = headerIdx + 1; i < aoa.length; i++) {
        const line = aoa[i];
        if (!line || line.every((v) => v === '' || v === null)) continue;
        const kode = String(line[cols.kode] ?? '').trim();
        if (!kode) continue;
        const nama = cols.nama !== undefined ? String(line[cols.nama] ?? '').trim() : '';
        const jenis = cols.jenis !== undefined ? String(line[cols.jenis] ?? '').trim() : '';
        // Lewati baris header berulang di dalam file (bukan produk)
        if (kode.toLowerCase() === 'kode item' || (nama.toLowerCase() === 'nama item' && jenis.toLowerCase() === 'jenis')) continue;
        const rak = cols.rak !== undefined ? String(line[cols.rak] ?? '').trim() : '';
        const satuan = cols.satuan !== undefined ? String(line[cols.satuan] ?? '').trim() : '';
        const stokRaw = cols.stok !== undefined ? line[cols.stok] : 0;
        const stok = Math.max(0, parseInt(Number(stokRaw) || 0, 10));
        if (seen.has(kode)) { duplicates++; const idx = seen.get(kode); rows[idx].stok += stok; }
        else {
          seen.set(kode, rows.length);
          rows.push({ kode, nama, jenis, rak, stok, satuan });
        }
      }
      setPreview({ rows, ignored, detected, duplicates, totalLines: aoa.length - 1 });
    } catch (err) {
      toast.error(`Gagal membaca Excel: ${err.message || err}`);
      setPreview(null);
    }
  };

  const canSubmit = preview && preview.rows.length > 0
    && (!hasExisting || confirmText.trim().toUpperCase() === 'RESET');

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await opnameUploadMaster({ rows: preview.rows });
      toast.success(`Master aktif: ${r.row_count} item`);
      onUploaded && onUploaded();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal unggah master');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Unggah Master Stok (Excel)</DialogTitle>
          <DialogDescription>
            Kolom yang dibaca: Kode Item, Nama Item, Jenis, Rak, Stok, Satuan. Kolom lain diabaikan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onPick} className="hidden"
              data-testid="opname-upload-file" />
            <span className="inline-flex items-center gap-2 border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-emerald-100">
              <Upload className="w-4 h-4" /> {file ? file.name : 'Pilih file .xlsx (maks 20 MB)'}
            </span>
          </label>
          {preview && (
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm space-y-2" data-testid="opname-preview">
              <div className="flex justify-between">
                <span className="text-stone-600">Baris terbaca</span>
                <span className="font-semibold">{preview.totalLines}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-600">Item unik (setelah gabung duplikat)</span>
                <span className="font-semibold text-emerald-800">{preview.rows.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-600">Duplikat digabung</span>
                <span className="font-semibold">{preview.duplicates}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-600">Kolom terdeteksi</span>
                <span className="font-mono text-xs">{preview.detected.join(', ')}</span>
              </div>
              {preview.ignored.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-stone-600">Kolom diabaikan</span>
                  <span className="font-mono text-xs text-stone-500">{preview.ignored.join(', ')}</span>
                </div>
              )}
            </div>
          )}
          {preview && hasExisting && (
            <div className="rounded-lg border border-red-200 bg-red-50/60 p-3 text-sm space-y-2" data-testid="opname-reset-warning">
              <div className="flex gap-2 items-start">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-red-800">Reset hasil hitung sebelumnya</div>
                  <div className="text-red-700 text-xs">
                    Master aktif akan diarsipkan otomatis (data & hasil fisik) supaya bisa diekspor bila salah unggah. Ketik <b>RESET</b> untuk melanjutkan.
                  </div>
                </div>
              </div>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Ketik RESET" className="h-9"
                data-testid="opname-reset-confirm" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
          <Button onClick={submit} disabled={!canSubmit || busy}
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            data-testid="opname-upload-submit">
            {busy ? 'Mengunggah…' : (hasExisting ? 'Reset & Unggah' : 'Unggah Master')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Row (input Lantai 1/2) ---------------- */
const RowInput = React.memo(function RowInput({ row, onSave }) {
  const [f1, setF1] = useState(row.floor1 == null ? '' : String(row.floor1));
  const [f2, setF2] = useState(row.floor2 == null ? '' : String(row.floor2));
  const [savingKey, setSavingKey] = useState('');
  const [savedKey, setSavedKey] = useState('');

  useEffect(() => {
    setF1(row.floor1 == null ? '' : String(row.floor1));
    setF2(row.floor2 == null ? '' : String(row.floor2));
  }, [row.floor1, row.floor2]);

  const commit = async (which, value) => {
    const orig = which === 'floor1' ? row.floor1 : row.floor2;
    const trimmed = String(value).trim();
    if (trimmed === '' && orig == null) return;
    if (trimmed === String(orig ?? '')) return;
    if (trimmed !== '' && !/^\d+$/.test(trimmed)) {
      toast.error('Hanya angka bulat non-negatif');
      if (which === 'floor1') setF1(orig == null ? '' : String(orig));
      else setF2(orig == null ? '' : String(orig));
      return;
    }
    setSavingKey(which);
    try {
      await onSave({ kode: row.kode, [which]: trimmed === '' ? null : parseInt(trimmed, 10) });
      setSavedKey(which);
      setTimeout(() => setSavedKey((k) => (k === which ? '' : k)), 1400);
    } finally { setSavingKey(''); }
  };

  const kbHandler = (which) => (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
  };
  const total = (parseInt(f1 || 0, 10) || 0) + (parseInt(f2 || 0, 10) || 0);
  const checked = f1 !== '' || f2 !== '';
  const selisih = checked ? total - (row.stok_sistem || 0) : null;
  const selCls = selisih == null ? 'text-stone-400'
    : selisih < 0 ? 'text-red-700 bg-red-50'
    : selisih > 0 ? 'text-amber-700 bg-amber-50'
    : 'text-emerald-700 bg-emerald-50';

  return (
    <tr className="border-b border-stone-100 hover:bg-stone-50/60" data-testid={`opname-row-${row.kode}`}>
      <td className="px-2 py-1.5 font-mono text-xs whitespace-nowrap">{row.kode}</td>
      <td className="px-2 py-1.5 text-sm text-stone-800">{row.nama || '—'}</td>
      <td className="px-2 py-1.5 text-xs text-stone-600 whitespace-nowrap">{row.jenis || '—'}</td>
      <td className="px-2 py-1.5 text-xs text-stone-600 whitespace-nowrap">{row.rak || '—'}</td>
      <td className="px-2 py-1.5 font-mono text-sm text-right whitespace-nowrap">{row.stok_sistem}</td>
      <td className="px-1 py-1">
        <div className="relative">
          <input value={f1} onChange={(e) => setF1(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={(e) => commit('floor1', e.target.value)}
            onKeyDown={kbHandler('floor1')}
            inputMode="numeric"
            className="w-full h-9 px-2 rounded border border-stone-300 text-right font-mono text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
            data-testid={`opname-floor1-${row.kode}`} />
          {savingKey === 'floor1' && <div className="absolute right-1 top-1 text-[9px] text-stone-400">…</div>}
          {savedKey === 'floor1' && <div className="absolute right-1 top-1 text-emerald-600"><Check className="w-3 h-3" /></div>}
        </div>
      </td>
      <td className="px-1 py-1">
        <div className="relative">
          <input value={f2} onChange={(e) => setF2(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={(e) => commit('floor2', e.target.value)}
            onKeyDown={kbHandler('floor2')}
            inputMode="numeric"
            className="w-full h-9 px-2 rounded border border-stone-300 text-right font-mono text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
            data-testid={`opname-floor2-${row.kode}`} />
          {savingKey === 'floor2' && <div className="absolute right-1 top-1 text-[9px] text-stone-400">…</div>}
          {savedKey === 'floor2' && <div className="absolute right-1 top-1 text-emerald-600"><Check className="w-3 h-3" /></div>}
        </div>
      </td>
      <td className="px-2 py-1.5 font-mono text-sm text-right whitespace-nowrap">{checked ? total : '—'}</td>
      <td className={`px-2 py-1.5 font-mono text-sm text-right whitespace-nowrap font-semibold rounded ${selCls}`}
        data-testid={`opname-selisih-${row.kode}`}>
        {selisih == null ? '—' : (selisih > 0 ? `+${selisih}` : selisih)}
      </td>
      <td className="px-2 py-1.5 text-[10px] text-stone-500 whitespace-nowrap">
        {row.updated_by ? <>{row.updated_by}<br />{fmtTime(row.updated_at)}</> : '—'}
      </td>
    </tr>
  );
});

/* ---------------- Settings Tab (admin) ---------------- */
function SettingsTab({ onDisplayNameChanged }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try { const r = await opnameSettingsGet(); setData(r); setName(r.display_name); }
    catch { toast.error('Gagal memuat pengaturan'); }
  };
  useEffect(() => { load(); }, []);
  const saveName = async () => {
    setSaving(true);
    try {
      const r = await opnameSettingsUpdate({ display_name: name });
      setData(r); setName(r.display_name);
      onDisplayNameChanged && onDisplayNameChanged(r.display_name);
      toast.success('Nama modul diperbarui');
    } catch { toast.error('Gagal simpan'); } finally { setSaving(false); }
  };
  const toggle = async (uid, enable) => {
    try { const r = await opnameSettingsUpdate({ toggle_user_id: uid, enable }); setData(r); }
    catch { toast.error('Gagal mengubah akses'); }
  };
  if (!data) return <div className="text-sm text-stone-500 py-8 text-center">Memuat…</div>;
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-stone-200 bg-white p-5">
        <div className="text-sm font-semibold text-stone-800 mb-2">Nama Tampilan Modul</div>
        <div className="flex gap-2 items-center">
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Stock Opname" className="h-10 max-w-md"
            data-testid="opname-display-name" />
          <Button onClick={saveName} disabled={saving || !name.trim()}
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            data-testid="opname-save-display-name">Simpan</Button>
        </div>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="text-sm font-semibold text-stone-800 px-5 py-3 border-b border-stone-100">
          Team Opname — beri akses per user
        </div>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-4 py-2">Username</th>
              <th className="text-left px-4 py-2">Nama</th>
              <th className="text-left px-4 py-2">Peran</th>
              <th className="text-center px-4 py-2">Team Opname</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-t border-stone-100" data-testid={`opname-user-${u.username}`}>
                <td className="px-4 py-2 font-mono text-xs">{u.username}</td>
                <td className="px-4 py-2">{u.full_name || '—'}</td>
                <td className="px-4 py-2 text-xs uppercase text-stone-600">{u.role}</td>
                <td className="px-4 py-2 text-center">
                  <input type="checkbox" checked={u.opname_enabled}
                    disabled={u.role === 'admin'}
                    onChange={(e) => toggle(u.id, e.target.checked)}
                    className="w-4 h-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`opname-toggle-${u.username}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Export Excel OPNAME ---------------- */
const exportOpnameToExcel = (rows) => {
  if (!rows.length) { toast.error('Belum ada item ber-selisih'); return; }
  const rpFmt = '#,##0';
  const header = { font: { bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
    alignment: { horizontal: 'center', vertical: 'center' } };
  const total = { font: { bold: true }, fill: { patternType: 'solid', fgColor: { rgb: 'FFECFDF5' } } };
  const aoa = [
    ['Kode', 'Nama', 'Stok Sistem', 'Total Fisik', 'Selisih', 'User', 'Updated At'].map((h) => ({ v: h, s: header })),
  ];
  let sumMin = 0, sumPlus = 0;
  rows.forEach((r) => {
    if ((r.selisih || 0) < 0) sumMin += r.selisih;
    else sumPlus += r.selisih;
    aoa.push([r.kode, r.nama || '',
      { v: r.stok_sistem, t: 'n', z: rpFmt },
      { v: r.total_fisik || 0, t: 'n', z: rpFmt },
      { v: r.selisih || 0, t: 'n', z: rpFmt },
      r.updated_by || '', r.updated_at || '']);
  });
  aoa.push([]);
  aoa.push([{ v: 'RINGKASAN', s: total }, '', '', '', '', '', '']);
  aoa.push(['Total Item', '', '', '', { v: rows.length, t: 'n' }, '', '']);
  aoa.push(['Total Minus', '', '', '', { v: sumMin, t: 'n', z: rpFmt }, '', '']);
  aoa.push(['Total Plus', '', '', '', { v: sumPlus, t: 'n', z: rpFmt }, '', '']);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 14 }, { wch: 32 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 22 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'OPNAME');
  const now = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const fname = `Opname_${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}_${p(now.getHours())}${p(now.getMinutes())}.xlsx`;
  XLSX.writeFile(wb, fname);
  toast.success(`Excel diunduh (${rows.length} item)`);
};

/* ---------------- MAIN PAGE ---------------- */
export default function StockOpnamePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [displayName, setDisplayName] = useState('Stock Opname');
  const [tab, setTab] = useState('hitung');
  const [master, setMaster] = useState(null);
  const [stats, setStats] = useState(null);
  const [facets, setFacets] = useState({ jenis: [], rak: [] });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [jenis, setJenis] = useState('');
  const [rak, setRak] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [openUpload, setOpenUpload] = useState(false);
  const [diffRows, setDiffRows] = useState([]);
  const [diffLoading, setDiffLoading] = useState(false);

  useEffect(() => {
    opnameModuleInfo().then((i) => setDisplayName(i.display_name)).catch(() => {});
    opnameMasterInfo().then((r) => setMaster(r.active)).catch(() => {});
    opnameFacets().then(setFacets).catch(() => {});
    opnameStats().then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await opnameRows({ search: debounced, jenis, rak, status, page, size });
      setRows(r.rows); setTotal(r.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat data');
    } finally { setLoading(false); }
  }, [debounced, jenis, rak, status, page, size]);

  useEffect(() => { if (tab === 'hitung') loadRows(); }, [tab, loadRows]);

  const loadDiff = useCallback(async () => {
    setDiffLoading(true);
    try { const r = await opnameListDiff(); setDiffRows(r.rows); }
    catch { toast.error('Gagal memuat OPNAME'); }
    finally { setDiffLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'opname') loadDiff(); }, [tab, loadDiff]);

  const saveCount = useCallback(async (payload) => {
    try {
      const updated = await opnameSaveCount(payload);
      setRows((prev) => prev.map((r) => (r.kode === updated.kode ? updated : r)));
      opnameStats().then(setStats).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal simpan');
      throw e;
    }
  }, []);

  const refreshAll = () => {
    opnameMasterInfo().then((r) => setMaster(r.active)).catch(() => {});
    opnameFacets().then(setFacets).catch(() => {});
    opnameStats().then(setStats).catch(() => {});
    if (tab === 'hitung') loadRows();
    if (tab === 'opname') loadDiff();
  };

  const totalPages = Math.max(1, Math.ceil(total / size));
  const progressPct = stats && stats.total > 0 ? Math.round(stats.checked * 100 / stats.total) : 0;

  return (
    <AccountingLayout fullWidth>
      <div className="space-y-5" data-testid="opname-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-800/10 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-emerald-800" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">{displayName}</h1>
              <p className="text-sm text-stone-500">
                Hitung ulang persediaan fisik toko dan bandingkan dengan stok sistem.
                {master && <span className="ml-2 text-xs">Master aktif: <b>{master.row_count}</b> item · {fmtDate(master.uploaded_at)} · {master.uploaded_by}</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshAll} className="gap-1.5" data-testid="opname-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
            {isAdmin && (
              <Button onClick={() => setOpenUpload(true)}
                className="bg-emerald-800 hover:bg-emerald-900 text-white gap-1.5"
                data-testid="opname-open-upload">
                <Upload className="w-4 h-4" /> Unggah Master
              </Button>
            )}
          </div>
        </div>

        {/* Dashboard */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="opname-dashboard">
            <div className="rounded-xl border border-stone-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1"><Package className="w-3 h-3" /> Total Item</div>
              <div className="text-2xl font-semibold text-stone-900 mt-1 font-mono">{stats.total}</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white px-4 py-3" data-testid="opname-stat-checked">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Sudah Dicek</div>
              <div className="text-2xl font-semibold text-emerald-800 mt-1 font-mono">{stats.checked}</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Belum Dicek</div>
              <div className="text-2xl font-semibold text-stone-900 mt-1 font-mono">{stats.unchecked}</div>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50/50 px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Minus</div>
              <div className="text-2xl font-semibold text-red-700 mt-1 font-mono">{stats.minus}</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Plus</div>
              <div className="text-2xl font-semibold text-amber-700 mt-1 font-mono">{stats.plus}</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Progress</div>
              <div className="text-lg font-semibold text-stone-800 mt-1 font-mono">{stats.checked} / {stats.total}</div>
              <div className="h-1.5 rounded-full bg-stone-100 mt-1.5 overflow-hidden">
                <div className="h-full bg-emerald-600" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="text-[10px] text-stone-500 mt-0.5">{progressPct}%</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-stone-200">
          <button onClick={() => setTab('hitung')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'hitung' ? 'border-emerald-800 text-emerald-800' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
            data-testid="opname-tab-hitung">Hitung Fisik</button>
          <button onClick={() => setTab('opname')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'opname' ? 'border-emerald-800 text-emerald-800' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
            data-testid="opname-tab-opname">OPNAME (Selisih)</button>
          {isAdmin && (
            <button onClick={() => setTab('settings')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1 ${tab === 'settings' ? 'border-emerald-800 text-emerald-800' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
              data-testid="opname-tab-settings"><Settings2 className="w-3.5 h-3.5" /> Pengaturan</button>
          )}
        </div>

        {tab === 'hitung' && (
          <div className="space-y-3">
            {/* Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 text-stone-400 absolute left-2.5 top-2.5" />
                <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Cari Kode / Nama…"
                  className="pl-9 h-9 w-64" data-testid="opname-search" />
              </div>
              <select value={jenis} onChange={(e) => { setJenis(e.target.value); setPage(1); }}
                className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white min-w-[130px]"
                data-testid="opname-filter-jenis">
                <option value="">Semua Jenis</option>
                {facets.jenis.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
              <select value={rak} onChange={(e) => { setRak(e.target.value); setPage(1); }}
                className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white min-w-[110px]"
                data-testid="opname-filter-rak">
                <option value="">Semua Rak</option>
                {facets.rak.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white"
                data-testid="opname-filter-status">
                <option value="all">Semua Status</option>
                <option value="checked">Sudah Dicek</option>
                <option value="unchecked">Belum Dicek</option>
                <option value="diff">Ada Selisih</option>
                <option value="minus">Minus</option>
                <option value="plus">Plus</option>
              </select>
              {(search || jenis || rak || status !== 'all') && (
                <button onClick={() => { setSearch(''); setJenis(''); setRak(''); setStatus('all'); setPage(1); }}
                  className="text-xs font-medium text-stone-600 hover:text-stone-900 px-2 py-1 rounded hover:bg-stone-100 inline-flex items-center gap-1">
                  <X className="w-3 h-3" /> Reset
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <select value={size} onChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(1); }}
                  className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white"
                  data-testid="opname-page-size">
                  <option value={50}>50 / hal</option>
                  <option value={100}>100 / hal</option>
                  <option value={200}>200 / hal</option>
                </select>
              </div>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
              <table className="w-full min-w-[1100px]">
                <thead className="bg-stone-50 sticky top-0 z-10">
                  <tr className="text-[11px] uppercase tracking-widest text-stone-500">
                    <th className="text-left px-2 py-2">Kode</th>
                    <th className="text-left px-2 py-2">Nama</th>
                    <th className="text-left px-2 py-2">Jenis</th>
                    <th className="text-left px-2 py-2">Rak</th>
                    <th className="text-right px-2 py-2">Stok Sistem</th>
                    <th className="text-right px-2 py-2 w-[95px]">Lantai 1</th>
                    <th className="text-right px-2 py-2 w-[95px]">Lantai 2</th>
                    <th className="text-right px-2 py-2">Total Fisik</th>
                    <th className="text-right px-2 py-2">Selisih</th>
                    <th className="text-left px-2 py-2">Update</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={10} className="text-center py-8 text-sm text-stone-500">Memuat…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={10} className="text-center py-8 text-sm text-stone-500">
                      {master ? 'Tidak ada data sesuai filter.' : 'Belum ada master aktif. Unggah Excel master untuk memulai.'}
                    </td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <RowInput key={r.kode} row={r} onSave={saveCount} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between text-xs text-stone-600">
              <span>{total} item · Halaman {page} / {totalPages}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="opname-page-prev">
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="opname-page-next">
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {tab === 'opname' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-stone-600">
                Item dengan selisih ≠ 0: <b>{diffRows.length}</b>
              </div>
              <Button onClick={() => exportOpnameToExcel(diffRows)}
                disabled={diffRows.length === 0}
                className="bg-emerald-800 hover:bg-emerald-900 text-white gap-1.5"
                data-testid="opname-export-excel">
                <Download className="w-4 h-4" /> Export Excel
              </Button>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-stone-50">
                  <tr className="text-[11px] uppercase tracking-widest text-stone-500">
                    <th className="text-left px-3 py-2">Kode</th>
                    <th className="text-left px-3 py-2">Nama</th>
                    <th className="text-right px-3 py-2">Stok Sistem</th>
                    <th className="text-right px-3 py-2">Total Fisik</th>
                    <th className="text-right px-3 py-2">Selisih</th>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-left px-3 py-2">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {diffLoading && <tr><td colSpan={7} className="text-center py-8 text-sm text-stone-500">Memuat…</td></tr>}
                  {!diffLoading && diffRows.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-8 text-sm text-stone-500">
                      Belum ada item ber-selisih.
                    </td></tr>
                  )}
                  {!diffLoading && diffRows.map((r) => {
                    const s = r.selisih || 0;
                    const cls = s < 0 ? 'text-red-700 bg-red-50' : 'text-amber-700 bg-amber-50';
                    return (
                      <tr key={r.kode} className="border-t border-stone-100" data-testid={`opname-diff-${r.kode}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.kode}</td>
                        <td className="px-3 py-2 text-sm">{r.nama || '—'}</td>
                        <td className="px-3 py-2 font-mono text-sm text-right">{r.stok_sistem}</td>
                        <td className="px-3 py-2 font-mono text-sm text-right">{r.total_fisik}</td>
                        <td className={`px-3 py-2 font-mono text-sm text-right font-semibold ${cls}`}>{s > 0 ? `+${s}` : s}</td>
                        <td className="px-3 py-2 text-xs text-stone-600">{r.updated_by || '—'}</td>
                        <td className="px-3 py-2 text-xs text-stone-500">{fmtDate(r.updated_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'settings' && isAdmin && (
          <SettingsTab onDisplayNameChanged={setDisplayName} />
        )}
      </div>

      <UploadMasterDialog
        open={openUpload} onClose={() => setOpenUpload(false)}
        hasExisting={!!master && stats && stats.checked > 0}
        onUploaded={refreshAll}
      />
    </AccountingLayout>
  );
}
